<template>
  <footer
    class="FooterComponent"
    role="contentinfo"
  >
    <nav
      class="content"
      :aria-label="ARIA_LABELS.FOOTER_NAV"
    >
      <BaseButton
        v-if="isMobile"
        type="transparent"
        :class="[openFooter ? 'open' : '']"
        name="copyright"
        id="copyright"
        :aria-expanded="String(openFooter)"
        @click="openFooterHandler"
      >
        {{ currentYear }} © asisa.es
      </BaseButton>
      <p
        v-else
        class="copyright"
      >
        {{ t(`${currentYear} &copy; asisa.es`) }}
      </p>

      <ul
        :class="[!isMobile || openFooter ? 'open' : '']"
        role="list"
      >
        <li
          v-for="(link, index) in footerLinks"
          :key="index"
        >
          <a
            :href="link.link"
            :title="link.title"
            :id="'footer-link-' + index"
            target="_blank"
            rel="noopener noreferrer"
            @click.prevent="goToUrl($event, link.link, link.title)"
          >
            {{ link.title }}
            <span class="sr-only">{{ ARIA_LABELS.NEW_WINDOW }}</span>
          </a>
        </li>
      </ul>
    </nav>
  </footer>
</template>

<script setup lang="ts">
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import { useIsMobile } from '@/modules/core/composables/useIsMobile'
  import { ARIA_LABELS } from '@/modules/core/constants/ariaLabels'
  import GTMEventType from '@/modules/core/models/GTMEventType'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import { useFooterStore } from '@/modules/core/stores/footerStore'
  import { useGTMStore } from '@/modules/core/stores/gtmStore'
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'

  const commonStore = useCommonStore()
  const footerStore = useFooterStore()
  const gmtStore = useGTMStore()
  const { t } = useI18n()
  const footerLinks = computed(() => commonStore.footerLinks)
  const currentYear = new Date().getFullYear()

  const { isMobile } = useIsMobile()

  const openFooter = computed({
    get: () => footerStore.getOpenFooter(),
    set: (value) => footerStore.setOpenFooter(value)
  })

  function openFooterHandler() {
    gmtStore.sendEvent({
      event: GTMEventType.MENU_FOOTER,
      click_name: 'copyright',
      click_id: 'copyright',
      btn_label: '2025 © asisa.es'
    })
    footerStore.setOpenFooter(!footerStore.getOpenFooter())
  }

  function goToUrl(event: Event, url: string, label: string) {
    event.preventDefault()
    gmtStore.sendEvent({
      event: GTMEventType.MENU_FOOTER,
      click_name: url,
      click_id: url,
      btn_label: label
    })
    window.open(url, '_blank')
  }
</script>

<style scoped lang="scss">
  .FooterComponent {
    width: $size-full;
    background-color: $color-primary-dark;
    padding: $spacing-l $spacing-xl;
    color: $color-neutral-00;
    margin-top: $spacing-56;
    z-index: $z-index-10;

    @include breakpoint-md {
      padding: $spacing-3xl $spacing-2xl;
    }

    .content {
      display: flex;
      align-items: stretch;
      flex-direction: column;
      width: $size-full;
      max-width: $size-1440;
      margin: 0 auto;

      @include breakpoint-md {
        flex-direction: row;
        align-items: center;
      }

      .BaseButton {
        justify-content: start;
        padding-left: 0 !important;
        padding-right: 0 !important;
        margin: 0;
        width: $size-full;
        max-width: $size-full;
        white-space: nowrap;
        color: $color-neutral-00 !important;

        &::after {
          content: var(--icon-chevron-down);
          font-family: $font-family-icons;
          font-size: $font-size-xl;
          margin-left: auto;
          transition: all 0.3s ease-in-out;
          transform: rotate(180deg);
        }

        &.open {
          &::after {
            transform: rotate(0);
          }
        }
      }

      .copyright {
        white-space: nowrap;

        @include breakpoint-md {
          line-height: $line-height-100p;
          margin-right: 8vw;
          align-self: flex-start;
        }

        @include breakpoint-md {
          line-height: $line-height-100p;
          margin-right: 3vw;
        }
      }

      ul {
        width: $size-full;
        flex: 0 auto;
        color: $color-neutral-00;
        flex-direction: column;
        list-style: none;
        justify-content: space-between;
        gap: $gap-m;
        display: flex;
        max-height: 0;
        overflow: hidden;
        opacity: 0;
        margin-top: 0;
        transition:
          max-height 0.1s ease-in-out,
          margin-top 0.1s linear,
          opacity 0.1s linear;

        @include breakpoint-md {
          flex-flow: row wrap;
          justify-content: flex-start;
          margin: 0;
          max-height: none;
          overflow: visible;
          opacity: 1;
        }

        @include breakpoint-xl {
          justify-content: space-between;
        }

        li {
          line-height: $line-height-43;

          @include breakpoint-md {
            line-height: $line-height-100p;
            flex-basis: 45%;
          }

          @include breakpoint-xl {
            flex-basis: auto;
          }
        }

        a {
          color: $color-neutral-00;
          text-decoration: none;
        }

        &.open {
          max-height: $size-screen-h-xxl;
          opacity: 1;
          overflow: visible;
          margin-top: $spacing-l;

          @include breakpoint-md {
            margin-top: 0;
          }
        }
      }
    }
  }
</style>
